import { useEffect, useState } from 'react'
import { fraudService, callApprovalService } from '../services/fraud'
import api from '../../../lib/api'
import type { FraudAlert, CashReconciliation, FraudAlertStats } from '../../../types'
import { SEVERITY_COLORS } from '../../../lib/constants'
import {
  Shield, AlertTriangle, CheckCircle, Search, IndianRupee,
  RefreshCw, Eye, X, ClipboardList, AlertCircle,
} from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { useWindowWidth } from '../../../lib/useWindowWidth'

type Tab = 'alerts' | 'reconciliation' | 'discounts'

const ALERT_STATUS_BADGE: Record<string, string> = {
  OPEN:           'badge-danger',
  INVESTIGATING:  'badge-warning',
  RESOLVED:       'badge-success',
  FALSE_POSITIVE: 'badge-neutral',
}

interface DiscountCall {
  id: number
  lead_id: number
  caller_name?: string
  od_discount_offered: number
  remarks: string | null
  approved_by?: number | null
  approved_at?: string | null
  created_at: string
}

export default function FraudAlertsPage() {
  const [tab, setTab] = useState<Tab>('alerts')
  const [alerts, setAlerts] = useState<FraudAlert[]>([])
  const [stats, setStats] = useState<FraudAlertStats | null>(null)
  const [recon, setRecon] = useState<CashReconciliation[]>([])
  const [discounts, setDiscounts] = useState<DiscountCall[]>([])
  const [runningAudit, setRunningAudit] = useState(false)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('OPEN')
  const [runningChecks, setRunningChecks] = useState(false)

  const [resolvingId, setResolvingId] = useState<number | null>(null)
  const [resolveNote, setResolveNote] = useState('')

  const [rejectingDiscountId, setRejectingDiscountId] = useState<number | null>(null)
  const [rejectDiscountReason, setRejectDiscountReason] = useState('')

  const [pageSuccess, setPageSuccess] = useState('')
  const [pageError, setPageError]     = useState('')

  const [reconAmount, setReconAmount] = useState('')
  const [reconNote, setReconNote] = useState('')
  const [reconSubmitting, setReconSubmitting] = useState(false)

  const loadAlerts = async () => {
    const [a, s] = await Promise.all([
      fraudService.listAlerts({ status: statusFilter || undefined }),
      fraudService.getAlertStats(),
    ])
    setAlerts(a)
    setStats(s)
  }

  const loadRecon = async () => {
    const data = await fraudService.listCashReconciliation()
    setRecon(data)
  }

  const loadDiscounts = async () => {
    try {
      const res = await api.get('/calls/pending-discounts')
      setDiscounts(res.data || [])
    } catch {
      setDiscounts([])
    }
  }

  const fetchAll = async () => {
    setLoading(true)
    try {
      await Promise.all([loadAlerts(), loadRecon(), loadDiscounts()])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [])
  useEffect(() => {
    if (tab === 'alerts') loadAlerts()
  }, [statusFilter])

  const handleInvestigate = async (id: number) => {
    await fraudService.investigateAlert(id)
    await loadAlerts()
  }

  const handleResolve = async () => {
    if (!resolvingId) return
    await fraudService.resolveAlert(resolvingId, resolveNote)
    setResolvingId(null)
    setResolveNote('')
    await loadAlerts()
  }

  const handleRunChecks = async () => {
    setRunningChecks(true)
    try {
      await fraudService.triggerAnomalyChecks()
      await loadAlerts()
    } finally {
      setRunningChecks(false)
    }
  }

  const handleRunSequenceAudit = async () => {
    setRunningAudit(true)
    try {
      const result = await fraudService.runSequenceAudit()
      setPageSuccess(`Sequence audit complete. ${result.alerts_created} new alert(s) created.`)
      setPageError('')
      await loadAlerts()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setPageError(detail || 'Sequence audit failed')
      setPageSuccess('')
    } finally {
      setRunningAudit(false)
    }
  }

  const handleSubmitRecon = async () => {
    const parsed = parseFloat(reconAmount)
    if (!reconAmount || isNaN(parsed) || parsed <= 0) {
      setPageError('Enter a valid positive amount')
      return
    }
    if (parsed > 1_000_000_000) {
      setPageError('Amount seems unreasonably high — please verify')
      return
    }
    setReconSubmitting(true)
    try {
      await fraudService.submitCashReconciliation({
        total_cash_deposited: Math.round(parsed * 100) / 100,
        variance_explanation: reconNote || undefined,
      })
      setReconAmount('')
      setReconNote('')
      await loadRecon()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setPageError(detail || 'Failed to submit reconciliation')
      setPageSuccess('')
    } finally {
      setReconSubmitting(false)
    }
  }

  const handleApproveDiscount = async (callId: number) => {
    await callApprovalService.approveDiscount(callId)
    await loadDiscounts()
  }

  const handleRejectDiscount = async () => {
    if (!rejectingDiscountId) return
    await callApprovalService.rejectDiscount(rejectingDiscountId, rejectDiscountReason)
    setRejectingDiscountId(null)
    setRejectDiscountReason('')
    await loadDiscounts()
  }

  const openAlerts = alerts.filter(a => a.status === 'OPEN').length
  const pendingDiscounts = discounts.filter(d => !d.approved_by)
  const isMobile = useWindowWidth() < 768

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Shield className="h-6 w-6" style={{ color: 'var(--danger-text)' }} />
            Fraud Control
          </h1>
          <p className="page-subtitle">Alerts, cash reconciliation, discount approvals</p>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-secondary"
            onClick={handleRunSequenceAudit}
            disabled={runningAudit}
          >
            <ClipboardList className={`h-3.5 w-3.5 ${runningAudit ? 'animate-pulse' : ''}`} />
            Sequence Audit
          </button>
          <button
            className="btn-secondary"
            onClick={handleRunChecks}
            disabled={runningChecks}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${runningChecks ? 'animate-spin' : ''}`} />
            Run Anomaly Checks
          </button>
        </div>
      </div>

      {pageSuccess && (
        <div className="alert-success">
          <CheckCircle className="h-4 w-4 shrink-0" />
          <span>{pageSuccess}</span>
          <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setPageSuccess('')}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {pageError && (
        <div className="alert-danger">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{pageError}</span>
          <button className="ml-auto opacity-60 hover:opacity-100" onClick={() => setPageError('')}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Open Alerts',       value: stats.by_status.open,             color: 'var(--danger-text)' },
            { label: 'Investigating',      value: stats.by_status.investigating,    color: 'var(--warning-text)' },
            { label: 'Resolved Today',     value: stats.by_status.resolved,   color: 'var(--success-text)' },
            { label: 'Pending Discounts',  value: pendingDiscounts.length, color: 'var(--orange-text)' },
          ].map(s => (
            <div key={s.label} className="card">
              <p className="stat-card-value" style={{ color: s.color }}>{s.value}</p>
              <p className="stat-card-label mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1" style={{ borderBottom: '1px solid var(--border-default)' }}>
        {([
          ['alerts', `Alerts${openAlerts > 0 ? ` (${openAlerts})` : ''}`, AlertTriangle],
          ['reconciliation', 'Cash Reconciliation', IndianRupee],
          ['discounts', `Discount Approvals${pendingDiscounts.length > 0 ? ` (${pendingDiscounts.length})` : ''}`, Shield],
        ] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            className="px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors"
            style={{
              borderBottomColor: tab === key ? 'var(--brand-primary)' : 'transparent',
              color: tab === key ? 'var(--brand-primary)' : 'var(--text-tertiary)',
            }}
            onClick={() => setTab(key)}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Alerts Tab */}
      {tab === 'alerts' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Search className="h-4 w-4 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
            <select
              className="input"
              style={{ maxWidth: '220px' }}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="INVESTIGATING">Investigating</option>
              <option value="RESOLVED">Resolved</option>
              <option value="FALSE_POSITIVE">False Positive</option>
            </select>
          </div>

          {loading ? (
            <div className="empty-state">Loading…</div>
          ) : alerts.length === 0 ? (
            <div className="empty-state">
              <CheckCircle className="h-10 w-10 mb-3" style={{ color: 'var(--success-text)' }} />
              <p>No alerts found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="card">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={SEVERITY_COLORS[alert.severity] || 'badge-neutral'}>
                          {alert.severity}
                        </span>
                        <span className={ALERT_STATUS_BADGE[alert.status] || 'badge-neutral'}>
                          {alert.status.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {alert.alert_type.replace(/_/g, ' ')}
                        </span>
                      </div>
                      {alert.description && (
                        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{alert.description}</p>
                      )}
                      {alert.evidence && Object.keys(alert.evidence).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs cursor-pointer hover:underline" style={{ color: 'var(--text-tertiary)' }}>
                            View evidence
                          </summary>
                          <pre
                            className="text-xs p-2 mt-1 rounded-md overflow-auto max-h-32"
                            style={{ background: 'var(--surface-subtle)', color: 'var(--text-secondary)' }}
                          >
                            {JSON.stringify(alert.evidence, null, 2)}
                          </pre>
                        </details>
                      )}
                      <p className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>
                        {new Date(alert.created_at).toLocaleString()}
                        {alert.resolved_at && ` · Resolved ${new Date(alert.resolved_at).toLocaleString()}`}
                      </p>
                    </div>
                    {(alert.status === 'OPEN' || alert.status === 'INVESTIGATING') && (
                      <div className="flex flex-col gap-2 shrink-0">
                        {alert.status === 'OPEN' && (
                          <button
                            className="btn-secondary"
                            onClick={() => handleInvestigate(alert.id)}
                          >
                            <Eye className="h-3.5 w-3.5" /> Investigate
                          </button>
                        )}
                        <button
                          className="btn-primary"
                          onClick={() => setResolvingId(alert.id)}
                        >
                          <CheckCircle className="h-3.5 w-3.5" /> Resolve
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cash Reconciliation Tab */}
      {tab === 'reconciliation' && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Submit Today's Cash Deposit
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">
                    Total Cash Deposited (₹) <span style={{ color: 'var(--danger-text)' }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="input"
                    value={reconAmount}
                    onChange={e => setReconAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="e.g. 45000"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Variance Explanation</label>
                  <input
                    type="text"
                    className="input"
                    value={reconNote}
                    onChange={e => setReconNote(e.target.value)}
                    placeholder="e.g. ₹500 outstanding — collecting tomorrow"
                  />
                </div>
              </div>
              <button
                className="btn-primary"
                onClick={handleSubmitRecon}
                disabled={reconSubmitting || !reconAmount}
              >
                {reconSubmitting ? 'Submitting…' : 'Submit Reconciliation'}
              </button>
            </div>
          </div>

          {recon.length > 0 && (
            isMobile ? (
              <div className="flex flex-col gap-2">
                {recon.map(r => (
                  <div key={r.id} className="card" style={{ padding: '10px 12px' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {new Date(r.reconciliation_date).toLocaleDateString()}
                      </span>
                      <span className={r.is_resolved ? 'badge-success' : 'badge-warning'}>
                        {r.is_resolved ? 'Resolved' : 'Pending'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div className="text-center">
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Collected</p>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>₹{r.total_cash_collected.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Deposited</p>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>₹{r.total_cash_deposited.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Variance</p>
                        <p className="text-sm font-medium" style={{ color: r.variance !== 0 ? 'var(--danger-text)' : 'var(--success-text)' }}>
                          {r.variance !== 0 ? `₹${r.variance.toLocaleString()}` : '—'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="table-card">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-right">Collected</th>
                        <th className="text-right">Deposited</th>
                        <th className="text-right">Variance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recon.map(r => (
                        <tr key={r.id}>
                          <td>{new Date(r.reconciliation_date).toLocaleDateString()}</td>
                          <td className="text-right">₹{r.total_cash_collected.toLocaleString()}</td>
                          <td className="text-right">₹{r.total_cash_deposited.toLocaleString()}</td>
                          <td
                            className="text-right font-medium"
                            style={{ color: r.variance !== 0 ? 'var(--danger-text)' : 'var(--success-text)' }}
                          >
                            {r.variance !== 0 ? `₹${r.variance.toLocaleString()}` : '—'}
                          </td>
                          <td>
                            <span className={r.is_resolved ? 'badge-success' : 'badge-warning'}>
                              {r.is_resolved ? 'Resolved' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Discount Approvals Tab */}
      {tab === 'discounts' && (
        <div className="space-y-4">
          {loading ? (
            <div className="empty-state">Loading…</div>
          ) : pendingDiscounts.length === 0 ? (
            <div className="empty-state">
              <CheckCircle className="h-10 w-10 mb-3" style={{ color: 'var(--success-text)' }} />
              <p>No pending discount approvals</p>
            </div>
          ) : (
            pendingDiscounts.map(d => (
              <div key={d.id} className="card flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg" style={{ color: 'var(--orange-text)' }}>
                      {d.od_discount_offered}% OD Discount
                    </span>
                    <span className="badge-warning">Needs Approval</span>
                  </div>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    {d.caller_name && `By ${d.caller_name} · `}
                    Lead #{d.lead_id}
                    {d.remarks && ` · "${d.remarks}"`}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    {new Date(d.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    className="btn-danger"
                    onClick={() => { setRejectingDiscountId(d.id); setRejectDiscountReason('') }}
                  >
                    <X className="h-3.5 w-3.5" /> Reject
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => handleApproveDiscount(d.id)}
                  >
                    <CheckCircle className="h-3.5 w-3.5" /> Approve
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Negative Confirmations Tab */}
      {/* Reject Discount Modal */}
      {rejectingDiscountId !== null && (
        <ModalOverlay onClose={() => setRejectingDiscountId(null)}>
          <div className="modal-panel" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                Reject Discount Request
              </h2>
              <button className="btn-icon" onClick={() => setRejectingDiscountId(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="form-group">
                <label className="form-label">Rejection Reason <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="text"
                  className="input"
                  value={rejectDiscountReason}
                  onChange={e => setRejectDiscountReason(e.target.value)}
                  placeholder="e.g. Discount exceeds policy limit"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button className="btn-secondary" onClick={() => setRejectingDiscountId(null)}>Cancel</button>
                <button className="btn-danger" onClick={handleRejectDiscount}>
                  <X className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Resolve Modal */}
      {resolvingId !== null && (
        <ModalOverlay onClose={() => { setResolvingId(null); setResolveNote('') }}>
          <div className="modal-panel" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Resolve Alert</h2>
              <button
                className="btn-icon"
                onClick={() => { setResolvingId(null); setResolveNote('') }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="form-group">
                <label className="form-label">Resolution Note</label>
                <textarea
                  className="input"
                  rows={3}
                  value={resolveNote}
                  onChange={e => setResolveNote(e.target.value)}
                  placeholder="Explain how this was resolved or why it's a false positive…"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  className="btn-secondary"
                  onClick={() => { setResolvingId(null); setResolveNote('') }}
                >
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleResolve}>
                  Mark Resolved
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}
